import { updateProfiles } from 'flow/actions/profile-actions';
import { List } from 'immutable';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useParams } from 'react-router-dom';
import ReferralNumberCombobox, {
    ReferralNumberOption,
} from 'flow/components/Setting/components/ReferralNumberCombobox';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import SettingSection from 'flow/components/Setting/SettingSection';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import DropdownMenuComponent from 'flow/components/ui-components/DropdownMenuComponent';
import Loading from 'flow/components/ui-components/Loading';
import RadioButton from 'flow/components/ui-components/RadioButton';
import Toggle from 'flow/components/ui-components/Toggle';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { PlusID, Profile } from 'flow/types/Profile';

import { TypedMap } from 'flow/types/TypedMap';
import { getCountryCode } from 'flow/utils/extension-utils';
import {
    getStandardProfileDescriptionInUserLanguage,
    hasSpokenReferralActiveOnProfile,
} from 'flow/utils/profile-utils';
import { twMerge } from 'tailwind-merge';
import formatDuration from '../functions/formatDuration';
import getReferralOptions from '../functions/getReferralOptions';
import onUpdate from '../functions/onUpdate';
import PrepareLandlineText from '../functions/prepareLandlineText';
import {
    buildReferralSoundUpdate,
    getForwardingNumberUpdates,
    getReferralTypeUpdates,
} from '../functions/referralUpdates';

interface Props {
    profiles: List<TypedMap<Profile>>;
    onClickBack: MouseEventHandler;
}

const MultipleLandline = ({ profiles, onClickBack }: Props) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const { openModal } = useModal();

    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const { contact } = useContact(extensionKey);
    const initialDelays = [5, 10, 15, 20, 25, 30, 45, 60, 90, 120];
    const profile = profiles.find((profile) => profile.get('plusId') === 1);
    const [showReferralNumberCombobox, setShowReferralNumberCombobox] =
        useState(false);
    const [selectedType, setSelectedType] = useState('');

    const [referralNumber, setReferralNumber] = useState<ReferralNumberOption>({
        dto: {},
        label: null,
    });

    useEffect(() => {
        setReferralNumber({
            label: profile.getIn([
                'callForwarding',
                'redirect',
                'nationalFormat',
            ]),
            dto: {},
        });
    }, [profile]);

    const referralSounds = useAppSelector((state) =>
        state.profiles.get('referal_sounds'),
    );

    const countryCode = getCountryCode(contact, null);

    if (!referralSounds || !countryCode) {
        return <Loading />;
    }

    const filteredProfiles = profiles.filter(
        (profile) => profile.get('plusId') !== PlusID.AVAILABLE,
    );
    const referAction = profile.getIn([
        'spokenNotification',
        'referPhraseType',
    ]);

    const unconditionalRedirectType = profile.getIn([
        'callForwarding',
        'unconditionalRedirect',
        'type',
    ]);

    const parallelCallType = profile.getIn([
        'parallelCalls',
        'parallelCallType',
    ]);

    const announceReferUntilTime = profile.getIn([
        'spokenNotification',
        'announceReferUntilTime',
    ]);

    const sound = referralSounds.find(
        (sound) =>
            sound.get('key') ===
            profile.getIn(['spokenNotification', 'referEntry', 'key']),
    );

    const displayComboBox =
        showReferralNumberCombobox || unconditionalRedirectType === 'number';

    const handleConfirmation = () => {
        openModal('confirmationDialog', {
            title: i18next.t('settings.reset.warning.general.Title', {
                currentSettings: i18next.t('settings.incoming_calls'),
            }),
            description: i18next.t(
                'settings.incoming_calls.profile.message.info',
            ),
            handleAccept: () => {
                dispatch(
                    updateProfiles(
                        extensionKey,
                        filteredProfiles.map((profile) =>
                            buildReferralSoundUpdate(profile),
                        ),
                    ),
                );
            },
        });
    };

    return (
        <Page onClickBack={onClickBack} header="settings.incoming_calls.fixed">
            <SettingSection>
                {i18next.t('settings.incoming_calls.profile.message')}
            </SettingSection>

            <div className="max-h-[230px] overflow-scroll">
                {filteredProfiles.map((profile) => (
                    <SettingRow key={profile.get('key')}>
                        <div
                            className={twMerge(
                                'size-4 rounded-full',
                                profile.get('available')
                                    ? 'bg-green'
                                    : 'bg-white ring-2 ring-gray-400 ring-inset',
                            )}
                        />
                        <p>
                            {getStandardProfileDescriptionInUserLanguage(
                                profile.get('description'),
                                i18next,
                            )}
                        </p>
                        <p className="ms-auto text-xs text-gray-500">
                            {profile.getIn([
                                'spokenNotification',
                                'referEntry',
                                'description',
                            ]) ||
                                i18next.t(
                                    'settings.incoming_calls.profile.message.none',
                                )}
                        </p>
                    </SettingRow>
                ))}
            </div>

            <p
                className="mt-2 mb-4 text-xs text-gray-500"
                dangerouslySetInnerHTML={{
                    __html: i18next.t(
                        'settings.incoming_calls.profile.message.info.spoken.referral',
                    ),
                }}
            />

            <Button
                onClick={handleConfirmation}
                disabled={
                    !filteredProfiles.some(
                        (profile) =>
                            !!profile.getIn([
                                'spokenNotification',
                                'referEntry',
                                'key',
                            ]),
                    )
                }
                color="primary"
                className="px-2 text-white"
            >
                {i18next.t(
                    'settings.incoming_calls.profile.message.info.spoken.referral.clear',
                )}
            </Button>

            <Divider />

            <div className={!sound ? 'opacity-50' : ''}>
                <SettingSection>
                    {i18next.t('settings.incoming_calls.profile.endtime.title')}
                </SettingSection>

                <SettingRow
                    cta={
                        <Toggle
                            disabled={!sound}
                            value={announceReferUntilTime}
                            onChange={(value) =>
                                dispatch(
                                    onUpdate(
                                        extensionKey,
                                        'profile',
                                        [
                                            {
                                                state: [
                                                    'spokenNotification',
                                                    'announceReferUntilTime',
                                                ],
                                                value,
                                            },
                                        ],
                                        profiles,
                                    ),
                                )
                            }
                            text={i18next.t(
                                'settings.incoming_calls.profile.endtime',
                            )}
                        />
                    }
                >
                    {i18next.t('settings.incoming_calls.profile.endtime')}
                </SettingRow>
            </div>

            <Divider />

            <div className={!sound ? 'opacity-50' : ''}>
                <SettingSection>
                    {i18next.t('settings.incoming_calls.profile.greeting')}
                </SettingSection>

                <DropdownMenuComponent
                    disabled={!sound}
                    title={i18next.t(
                        `settings.incoming_calls.profile.greeting.${referAction}`,
                    )}
                    onItemSelected={(value) =>
                        dispatch(
                            onUpdate(
                                extensionKey,
                                'profile',
                                [
                                    {
                                        state: [
                                            'spokenNotification',
                                            'referPhraseType',
                                        ],
                                        value,
                                    },
                                ],
                                profiles,
                            ),
                        )
                    }
                    options={['firstname', 'both', 'standard'].map((key) => ({
                        key,
                        label: i18next.t(
                            `settings.incoming_calls.profile.greeting.${key}`,
                        ),
                    }))}
                />
            </div>

            <Divider />

            <SettingSection>
                {i18next.t('settings.incoming_calls.profile.referal')}
            </SettingSection>

            <DropdownMenuComponent
                title={
                    showReferralNumberCombobox
                        ? i18next.t('settings.incoming_calls.delayed.number')
                        : PrepareLandlineText({
                              profile,
                              onlyRefer: true,
                              countryCode,
                              selectedType,
                          })
                }
                onItemSelected={(type) => {
                    setSelectedType(type);
                    if (type === 'number') {
                        setShowReferralNumberCombobox(true);
                        return;
                    }
                    dispatch(
                        onUpdate(
                            extensionKey,
                            'profile',
                            getReferralTypeUpdates(type),
                            profiles,
                        ),
                    );

                    setShowReferralNumberCombobox(false);
                }}
                options={getReferralOptions(
                    filteredProfiles.every(hasSpokenReferralActiveOnProfile),
                ).map((key) => ({
                    key,
                    label: i18next.t(
                        `settings.incoming_calls.profile.referal.${key}`,
                    ),
                }))}
            />

            {displayComboBox && (
                <div className="mt-4">
                    <ReferralNumberCombobox
                        ariaLabel={i18next.t(
                            'settings.callrouting.action.picknumber',
                        )}
                        referralNumber={referralNumber}
                        onChange={(option) => {
                            setReferralNumber(option);
                            dispatch(
                                onUpdate(
                                    extensionKey,
                                    'profile',
                                    getForwardingNumberUpdates(option.dto),
                                    profiles,
                                ),
                            );
                        }}
                    />
                </div>
            )}

            <Divider />

            {selectedType !== 'number' &&
                parallelCallType === 'both' &&
                unconditionalRedirectType === 'busy' && (
                    <>
                        <SettingSection>
                            {i18next.t(
                                'settings.incoming_calls.optionlist.delay',
                            )}
                        </SettingSection>

                        {profile
                            .getIn(['parallelCalls', 'delayTypeOptions'])
                            .map((delayTypeOption) => {
                                const getDelayTitle = () => {
                                    if (delayTypeOption === 'no_delay') {
                                        return i18next.t(
                                            'settings.incoming_calls.delay.none',
                                        );
                                    }

                                    if (delayTypeOption === 'mobile') {
                                        return i18next.t(
                                            'settings.incoming_calls.optionlist.delay_mobile',
                                        );
                                    }

                                    return i18next.t(
                                        'settings.incoming_calls.optionlist.delay_landline',
                                    );
                                };

                                return (
                                    <RadioButton
                                        key={`delayTypeOptions-${delayTypeOption}`}
                                        name="delayTypeOptions"
                                        checked={
                                            profile.getIn([
                                                'parallelCalls',
                                                'delayType',
                                            ]) === delayTypeOption
                                        }
                                        onChange={() =>
                                            dispatch(
                                                onUpdate(
                                                    extensionKey,
                                                    'profile',
                                                    [
                                                        {
                                                            state: [
                                                                'parallelCalls',
                                                                'delayType',
                                                            ],
                                                            value: delayTypeOption,
                                                        },
                                                        {
                                                            state: [
                                                                'parallelCalls',
                                                                'mexDelay',
                                                            ],
                                                            value: 5,
                                                        },
                                                    ],
                                                    profiles,
                                                    profile,
                                                ),
                                            )
                                        }
                                    >
                                        {getDelayTitle()}
                                    </RadioButton>
                                );
                            })}

                        {profile.getIn(['parallelCalls', 'delayType']) !==
                            'no_delay' && (
                            <DropdownMenuComponent
                                title={`${i18next.t(
                                    'settings.incoming_calls.optionlist.delay_time',
                                )} ( ${formatDuration(
                                    profile.getIn([
                                        'parallelCalls',
                                        'mexDelay',
                                    ]),
                                )} )`}
                                options={initialDelays.map((item) => ({
                                    key: item.toString(),
                                    label: formatDuration(item),
                                }))}
                                onItemSelected={(value) =>
                                    dispatch(
                                        onUpdate(
                                            extensionKey,
                                            'profile',
                                            [
                                                {
                                                    state: [
                                                        'parallelCalls',
                                                        'mexDelay',
                                                    ],
                                                    value,
                                                },
                                            ],
                                            profiles,
                                            profile,
                                        ),
                                    )
                                }
                            />
                        )}
                    </>
                )}

            {selectedType !== 'number' &&
                unconditionalRedirectType === 'busy' &&
                (parallelCallType === 'fixed' ||
                    parallelCallType === 'both') && (
                    <>
                        <SettingSection>
                            {i18next.t('settings.advanced')}
                        </SettingSection>

                        <SettingRow
                            information={i18next.t(
                                'settings.incoming_calls_mvoip.warning',
                            )}
                            cta={
                                <Toggle
                                    value={
                                        profiles
                                            .filter((profile) =>
                                                profile.getIn(['callMVoip']),
                                            )
                                            .toArray().length ===
                                        profiles.toArray().length
                                    }
                                    onChange={(value) =>
                                        dispatch(
                                            onUpdate(
                                                extensionKey,
                                                'profile',
                                                [
                                                    {
                                                        state: ['callMVoip'],
                                                        value,
                                                    },
                                                ],
                                                profiles,
                                            ),
                                        )
                                    }
                                    text={i18next.t(
                                        'settings.incoming_calls.mvoip',
                                    )}
                                />
                            }
                        >
                            {i18next.t('settings.incoming_calls.mvoip')}
                        </SettingRow>
                    </>
                )}
        </Page>
    );
};

export default MultipleLandline;
